// Data access for the USER SE "Penerimaan QR" page. The two receipt flows are
// served by separate endpoints (`/api/qrs/receipt/se/dealer` and
// `/api/qrs/receipt/se/pos`), and the page balance by
// `/api/qrs/receipt/se/saldo`.

import { FLOW_DEALER_POS, FLOW_MD_DEALER } from "./qr-issue";

const INVALID_PAGE_MESSAGE = "Format respons penerimaan QR tidak valid";
const INVALID_SALDO_MESSAGE = "Format respons saldo QR tidak valid";

// The ONLY place a UI flow is mapped to its list endpoint.
const RECEIPT_ENDPOINTS = {
  [FLOW_MD_DEALER]: "/api/qrs/receipt/se/dealer",
  [FLOW_DEALER_POS]: "/api/qrs/receipt/se/pos",
};

const SALDO_ENDPOINT = "/api/qrs/receipt/se/saldo";

// Maps each UI flow to its balance key in the saldo response.
const SALDO_KEYS = {
  [FLOW_MD_DEALER]: "dealer",
  [FLOW_DEALER_POS]: "pos",
};

function toCount(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function compactParams(params) {
  const result = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      result[key] = value;
    }
  });
  return result;
}

function parseReceiptRow(item) {
  if (!item || typeof item !== "object") {
    throw new Error(INVALID_PAGE_MESSAGE);
  }
  return {
    id: item.id,
    bastNo: item.bast_no || "",
    dealerCode: item.dealer_code || "",
    dealerName: item.dealer_name || "",
    posCode: item.pos_code || "",
    posName: item.pos_name || "",
    qty: toCount(item.qty),
    deliveredAt: item.delivery_at || null,
    status: item.status || "",
    receivedAt: item.received_at || null,
  };
}

/**
 * Extracts one page of receipts from the `GET /api/qrs/receipt/se/{dealer,pos}`
 * body. POS fields are empty strings for the MD → Dealer flow.
 * @param {unknown} body Response body with `data` rows and pagination `meta`.
 * @returns {{
 *   rows: {
 *     id: number, bastNo: string, dealerCode: string, dealerName: string,
 *     posCode: string, posName: string, qty: number, deliveredAt: string | null,
 *     status: string, receivedAt: string | null,
 *   }[],
 *   total: number,
 *   lastPage: number,
 * }}
 */
export function parseReceiptPage(body) {
  const data = body ? body.data : undefined;
  const meta = body ? body.meta : undefined;
  if (!Array.isArray(data) || !meta) {
    throw new Error(INVALID_PAGE_MESSAGE);
  }
  return {
    rows: data.map((item) => parseReceiptRow(item)),
    total: toCount(meta.total),
    lastPage: toCount(meta.lastPage),
  };
}

/**
 * Fetches one page of receipts for one flow. Empty params are dropped.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @param {{
 *   flow: string, startDate?: string, endDate?: string, date?: string,
 *   year?: number | string, month?: number | string, status?: string,
 *   search?: string, dealerCode?: string, dealerType?: string,
 *   karesidenan?: string, city?: string, dayDiffMin?: number,
 *   dayDiffMax?: number, page?: number, limit?: number, sortBy?: string,
 *   sortOrder?: string,
 * }} query
 * @returns {Promise<ReturnType<typeof parseReceiptPage>>}
 * @throws {Error} When `query.flow` is not a known flow.
 */
export async function fetchReceiptPage(http, query) {
  const endpoint = RECEIPT_ENDPOINTS[query.flow];
  if (!endpoint) {
    throw new Error(`Alur penerimaan QR tidak dikenal: ${query.flow}`);
  }
  const params = compactParams({
    status: query.status,
    search: (query.search || "").trim(),
    kode_dealer: query.dealerCode,
    dealer_type: query.dealerType,
    karesidenan: query.karesidenan,
    kota: query.city,
    start_date: query.startDate,
    end_date: query.endDate,
    date: query.date,
    year: query.year,
    month: query.month,
    day_diff_min: query.dayDiffMin,
    day_diff_max: query.dayDiffMax,
    page: query.page,
    limit: query.limit,
    sort_by: query.sortBy,
    sort_order: query.sortOrder,
  });
  const resp = await http.get(endpoint, { params });
  return parseReceiptPage(resp.data);
}

/**
 * Extracts the QR balances from the `GET /api/qrs/receipt/se/saldo` body.
 * Only the `saldo` of each block is read; the movement breakdown is ignored.
 * @param {unknown} body Response body, e.g.
 *   `{ data: { dealer: { saldo }, pos: { saldo }, total } }`.
 * @returns {{ dealer: number, pos: number, total: number }}
 */
export function parseReceiptSaldo(body) {
  const data = body ? body.data : undefined;
  if (
    !data ||
    typeof data !== "object" ||
    !data.dealer ||
    typeof data.dealer !== "object" ||
    !data.pos ||
    typeof data.pos !== "object"
  ) {
    throw new Error(INVALID_SALDO_MESSAGE);
  }
  return {
    dealer: toCount(data.dealer.saldo),
    pos: toCount(data.pos.saldo),
    total: toCount(data.total),
  };
}

/**
 * Fetches the QR balances of the Dealer and POS stock.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @returns {Promise<ReturnType<typeof parseReceiptSaldo>>}
 */
export async function fetchReceiptSaldo(http) {
  const resp = await http.get(SALDO_ENDPOINT);
  return parseReceiptSaldo(resp.data);
}

/**
 * Picks the balance shown for the active flow tab.
 * @param {{ dealer: number, pos: number, total: number }} saldo Parsed saldo.
 * @param {string} flow `FLOW_MD_DEALER` or `FLOW_DEALER_POS`.
 * @returns {number}
 * @throws {Error} When `flow` is not a known flow.
 */
export function pickSaldoForFlow(saldo, flow) {
  const key = SALDO_KEYS[flow];
  if (!key) {
    throw new Error(`Alur penerimaan QR tidak dikenal: ${flow}`);
  }
  return saldo[key];
}
